import { Sentry } from './sentry';

/**
 * Log level constants
 * @constant {Object}
 * @property {string} DEBUG - Debug level logging
 * @property {string} INFO - Info level logging
 * @property {string} WARN - Warning level logging
 * @property {string} ERROR - Error level logging
 */
const LOG_LEVELS = {
	DEBUG: 'debug',
	INFO: 'info',
	WARN: 'warn',
	ERROR: 'error'
};

/**
 * Centralized logger with Sentry integration
 * @class Logger
 * @example
 * import { logger } from 'common-utilities';
 * logger.info('User logged in', { userId: 123 });
 * logger.error('Payment failed', error, { orderId: 456 });
 */
class Logger {
	constructor() {
		this.isDevelopment = process.env.NODE_ENV === 'development';
	}

	/**
	 * Format log message for output
	 * @private
	 * @param {string} level - Log level
	 * @param {string|Object} message - Message to log
	 * @param {...*} args - Additional arguments
	 * @returns {Object} Formatted log object
	 */
	_formatMessage(level, message, ...args) {
		if (this.isDevelopment) {
			// Development: human-readable output
			return { message, args, useConsole: true };
		}

		// Production: structured JSON output
		const log = {
			timestamp: new Date().toISOString(),
			level,
			message: typeof message === 'string' ? message : JSON.stringify(message),
			environment: process.env.REACT_APP_ENVIRONMENT || 'unknown'
		};

		if (args.length > 0) {
			log.context = args;
		}

		return { formatted: JSON.stringify(log), useConsole: false };
	}

	/**
	 * Log debug message (development only)
	 * @param {string|Object} message - Message to log
	 * @param {...*} args - Additional arguments
	 * @example
	 * logger.debug('Cache hit', { key: 'user-123', ttl: 3600 });
	 */
	debug(message, ...args) {
		const formatted = this._formatMessage('debug', message, ...args);

		if (formatted.useConsole) {
			// eslint-disable-next-line no-console
			console.debug(formatted.message, ...formatted.args);
		} else {
			// eslint-disable-next-line no-console
			console.log(formatted.formatted);
		}
	}

	/**
	 * Log info message and add breadcrumb to Sentry
	 * @param {string|Object} message - Message to log
	 * @param {...*} args - Additional arguments
	 * @example
	 * logger.info('User logged in', { userId: 123, method: 'email' });
	 */
	info(message, ...args) {
		const formatted = this._formatMessage('info', message, ...args);

		if (formatted.useConsole) {
			// eslint-disable-next-line no-console
			console.info(formatted.message, ...formatted.args);
		} else {
			// eslint-disable-next-line no-console
			console.log(formatted.formatted);
		}

		Sentry.addBreadcrumb({
			message: typeof message === 'string' ? message : JSON.stringify(message),
			level: 'info',
			data: args.length > 0 ? { args } : undefined
		});
	}

	/**
	 * Log warning message and add breadcrumb to Sentry
	 * @param {string|Object} message - Message to log
	 * @param {...*} args - Additional arguments
	 * @example
	 * logger.warn('API rate limit approaching', { remaining: 10 });
	 */
	warn(message, ...args) {
		const formatted = this._formatMessage('warn', message, ...args);

		if (formatted.useConsole) {
			// eslint-disable-next-line no-console
			console.warn(formatted.message, ...formatted.args);
		} else {
			// eslint-disable-next-line no-console
			console.log(formatted.formatted);
		}

		Sentry.addBreadcrumb({
			message: typeof message === 'string' ? message : JSON.stringify(message),
			level: 'warning',
			data: args.length > 0 ? { args } : undefined
		});
	}

	/**
	 * Log error message, report to Sentry, and add breadcrumb
	 * @param {string|Object} message - Error message
	 * @param {Error} error - Error object to capture
	 * @param {...*} args - Additional context
	 * @example
	 * try {
	 *   riskyOperation();
	 * } catch (error) {
	 *   logger.error('Operation failed', error, { userId: 123 });
	 * }
	 */
	error(message, error, ...args) {
		const formatted = this._formatMessage('error', message, ...args);

		if (formatted.useConsole) {
			// eslint-disable-next-line no-console
			console.error(formatted.message, error, ...formatted.args);
		} else {
			// eslint-disable-next-line no-console
			console.log(formatted.formatted);
		}

		Sentry.captureException(error || new Error(message), {
			extra: {
				message,
				args: args.length > 0 ? args : undefined
			}
		});
	}

	log(message, ...args) {
		// Alias for info
		this.info(message, ...args);
	}
}

export const logger = new Logger();
export default logger;
